import { useNavigate, useParams } from "react-router-dom";
import {
    AvailableBXGYTemplates,
    BundlePositionsTypes,
    BundleTypes,
    DiscountTypes,
    generateRandomUniqueId,
    nonEmptyOverride,
    RedirectOptionsTypes,
    updateBundleConfigWithRealTimeProducts,
    validateFontSize,
    validateHexalColor,
    WidgetShowOptionsTypes,
} from "../constants";
import FormPreviewWrapper from "../../../components/FormPreviewWrapper/FormPreviewWrapper";
import styles from "./Bxgy.module.css";
import { endPoints, getData } from "../../../utilities/networkCalls";
import { createContext, Dispatch, useContext, useEffect, useState, useRef, useMemo } from "react";
import { getBundleRoute, MAX_LENGTH_DESKTOP, MAX_LENGTH_MOBILE, flattenProducts } from "../../constants";
import { useField, useForm } from "@shopify/react-form";
import {
    Action,
    ChangeEvents,
    BxgyConfigInterface,
    BxgyDiscountedVariantMode,
    VolumeConfigInterface,
    ProductObj,
} from "../Types";
import { AppConfigContext } from "../../../context/AppConfigContext";
import { getUpdatedTargeting, validateTargeting } from "../../shouldRenderTargeting";
import useBxgyHook from "./useBxgyHook";
import { BxgyPreview } from "./BxgyBundle/BxgyBundle";
import BxgyContentForm from "./BxgyContentForm";
import BxgyDesignForm from "./BxgyDesignForm";
import BxgyTargetForm from "./BxgyTargetForm";
import BxgySettingsForm from "./BxgySettingsForm";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import { scrollToTop } from "../../../utilities/functions";
import { useInspectorMode } from "../../../hooks/useInspectorMode";
import TemplateSelectionScreen from "../../../components/TemplateSelectionScreen/TemplateSelectionScreen";

export interface BxgyBundleContextType {
    bundleConfig?: BxgyConfigInterface;
    dispatchBundleConfig: Dispatch<Action<BxgyConfigInterface>>;
    handleNext: (step: number, stepName: string) => void;
    fields: any;
    submit: any;
    isMobile: boolean;
    validate: any;
    SwatchesBundleConfigTouchedRef: React.MutableRefObject<Record<string, string>>;
    // --- inspector mode additions ---
    inspectorMode?: boolean;
    setInspectorMode?: Dispatch<React.SetStateAction<boolean>>;
    inspectorTarget: any | null;
}

export const BxgyBundleContext = createContext<BxgyBundleContextType>(null as any);

const Bxgy = ({
    forAiBundle = false,
    preConfig = "",
    setPreConfig = () => {},
    formPreviewWrapperRef,
    setFormState = () => {},
    setSavingBundle = () => {},
    onlyPreview = false,
}: {
    preConfig?: string;
    forAiBundle?: boolean;
    formPreviewWrapperRef?: any;
    onlyPreview?: boolean;
    setPreConfig?: Dispatch<any>;
    setFormState?: any;
    setSavingBundle?: any;
}) => {
    const { bundleConfig, dispatchBundleConfig } = useBxgyHook();
    const [showBundlerLevel, setShowBundlerLevel] = useState(false);

    const [_config, setConfig] = useState({
        ...bundleConfig,
    });
    const { trackFromTabs } = useBundleMixpanelHook();
    const SwatchesBundleConfigTouchedRef = useRef<Record<string, string>>({});
    const isProductPageVisibleRef = useRef(bundleConfig.isProductPageVisible);
    isProductPageVisibleRef.current = bundleConfig.isProductPageVisible;

    const allow_free_gifts_ref = useRef(bundleConfig.allow_free_gifts);
    allow_free_gifts_ref.current = bundleConfig.allow_free_gifts;

    const {
        fields,
        submit,
        // submitting,
        dirty,
        reset,
        submitErrors,
        makeClean,
        validate,
    } = useForm({
        fields: {
            isProductPageVisible: useField(_config?.isProductPageVisible),
            allowed_product_pages: useField(_config?.allowed_product_pages),
            render_product_pages_type: useField(_config?.render_product_pages_type),
            render_embed_on_visibility: useField(_config?.render_embed_on_visibility),
            hide_native_elements: useField(_config?.hide_native_elements || ""),
            bundle_as_product: {
                product_title: useField({
                    value: _config?.bundle_as_product?.product_title || "",
                    validates: (val) => {
                        if (isProductPageVisibleRef.current) {
                            if (!val) {
                                return "Bundle Product Title is required";
                            }
                            if (val.length < 3) {
                                return "Bundle Product Title must be at least 3 characters long";
                            }
                        }
                        return undefined;
                    },
                }),
                product_description: useField(_config?.bundle_as_product?.product_description || ""),
                images: useField(_config?.bundle_as_product?.images || []),
                show_on_product_page: useField(_config?.bundle_as_product?.show_on_product_page || []),
                product_handle: useField(_config?.bundle_as_product?.product_handle || ""),
                double_discount: useField(_config?.bundle_as_product?.double_discount || false),
                collections: useField(_config?.bundle_as_product?.collections || []),
            },
            show_product_quantity: useField(_config?.show_product_quantity ?? true),
            discounted_variant_mode: useField<BxgyDiscountedVariantMode>(
                _config?.discounted_variant_mode ?? "customer_chooses",
            ),
            title: useField({
                value: _config?.title,
                validates: [(val) => nonEmptyOverride(val, "Title is required")],
            }),
            offer_name: useField(_config?.offer_name),
            discount_label_template: useField({
                value: _config?.discount_label_template,
                validates: [(val) => nonEmptyOverride(val, "Discount label is required")],
            }),
            should_flatten_products: useField(_config?.should_flatten_products),
            description: useField(_config?.description),
            bundle_product_level: useField<"product" | "variant">(_config?.bundle_product_level),
            products: useField({
                value: _config?.products,
                validates: [
                    (products) => {
                        if (products.length <= 0) {
                            return "Products are required";
                        }
                    },
                ],
            }),
            discounted_products: useField({
                value: _config?.discounted_products,
                validates: [
                    (dProducts) => {
                        if (dProducts.length <= 0) {
                            return "Discounted products are required";
                        }
                    },
                ],
            }),
            discount_type: useField<DiscountTypes>({
                value: _config?.discount_type,
                validates: [(val) => nonEmptyOverride(val, "Type is required")],
            }),
            discount_value: useField({
                value: _config?.discount_value as unknown as string,
                validates: [
                    (val) => nonEmptyOverride(val, "Discount value is required"),
                    (value) => {
                        if (fields?.discount_type?.value == "none") {
                            return "";
                        }
                        if (fields?.discount_type?.value == "percentage" && +value > 100) {
                            return "Invalid discount";
                        }
                        if (+value < 0) {
                            return "Invalid discount";
                        }
                    },
                ],
            }),
            order_discounts: useField(_config?.order_discounts),
            product_discounts: useField(_config?.product_discounts),
            shipping_discounts: useField(_config?.shipping_discounts),
            add_to_cart_cta_text: useField({
                value: _config?.add_to_cart_cta_text,
                validates: [(val) => nonEmptyOverride(val, "CTA value is required")],
            }),
            y_label_get_text: useField({
                value: _config?.y_label_get_text,
                validates: [(val) => nonEmptyOverride(val, "Get text is required")],
            }),
            y_label_off_text: useField({
                value: _config?.y_label_off_text,
                validates: [(val) => nonEmptyOverride(val, "Off text is required")],
            }),
            free_text: useField({
                value: _config?.free_text,
                validates: [(val) => nonEmptyOverride(val, "Free text is required")],
            }),
            x_label_buy_text: useField({
                value: _config?.x_label_buy_text,
                validates: [(val) => nonEmptyOverride(val, "Buy text is required")],
            }),

            quantity_text: useField({
                value: _config?.quantity_text,
                validates: [(val) => nonEmptyOverride(val, "Quantity text is required")],
            }),
            footer_total_text: useField({
                value: _config?.footer_total_text,
                validates: [(val) => nonEmptyOverride(val, "Footer total text is required")],
            }),
            start_date: useField(_config?.start_date),
            end_date: useField(_config?.end_date || null),
            use_compare_at_price: useField(_config?.use_compare_at_price || false),
            bundle_embed_id: useField(_config?.bundle_embed_id),
            show_swatches: useField(_config?.show_swatches),
            swatchesProductsMap: useField({
                value: _config?.swatchesProductsMap,
                validates: [],
                dirtyStateComparator: (v1, v2) => {
                    const res = JSON.stringify(v1) != JSON.stringify(v2);
                    return res;
                },
            }),
            swatchOptions: useField({
                value: _config?.swatchOptions,
                validates: [],
                dirtyStateComparator: (v1, v2) => {
                    return JSON.stringify(v1) != JSON.stringify(v2);
                },
            }),
            swatchShape: useField(_config?.swatchShape),
            swatchSize: useField(_config?.swatchSize),
            allow_free_gifts: useField(_config?.allow_free_gifts),
            free_gifts: useField({
                value: _config?.free_gifts,
                validates: [
                    (fg: ProductObj[]) => {
                        console._log(allow_free_gifts_ref.current);
                        if (allow_free_gifts_ref.current) {
                            if (fg.length <= 0) {
                                return "Free gift is required";
                            }

                            for (const p of fg) {
                                if (p.quantity < 0) {
                                    return "Free gift quantity cannot be negative";
                                }
                                if (p.quantity === 0) {
                                    return "Free gift quantity cannot be zero";
                                }
                            }
                        }
                        return undefined;
                    },
                ],
            }),
            free_gift_text: useField({
                value: _config?.free_gift_text,
                validates: [(val) => nonEmptyOverride(val, "Free gift Title is required")],
            }),
            selected_template_bxgy: useField(
                _config?.selected_template_bxgy || AvailableBXGYTemplates.VERTICAL_CLASSIC,
            ),

            bundles_design: {
                default_cta: useField<boolean>(_config?.bundles_design?.default_cta),
                add_to_cart_bg_color: useField({
                    value: _config?.bundles_design?.add_to_cart_bg_color,
                    validates: [(val) => validateHexalColor(val, "Add to cart background")],
                }),
                swatches_select_color: useField({
                    value: _config?.bundles_design?.swatches_select_color,
                    validates: [(val) => validateHexalColor(val, "Swatches")],
                }),
                add_to_cart_border_radius: useField(
                    _config?.bundles_design?.add_to_cart_border_radius as unknown as string,
                ),
                add_to_cart_border_color: useField({
                    value: _config?.bundles_design?.add_to_cart_border_color,
                    validates: [(val) => validateHexalColor(val, "Add to cart border")],
                }),
                add_to_cart_border_width: useField(
                    _config?.bundles_design?.add_to_cart_border_width as unknown as string,
                ),
                add_to_cart_text_color: useField({
                    value: _config?.bundles_design?.add_to_cart_text_color,
                    validates: [(val) => validateHexalColor(val, "Add to cart text")],
                }),
                add_to_cart_text_size: useField({
                    value: _config?.bundles_design?.add_to_cart_text_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Add to cart text font")],
                }),
                add_to_cart_text_style: useField(_config?.bundles_design?.add_to_cart_text_style),
                bundle_title_color: useField({
                    value: _config?.bundles_design?.bundle_title_color,
                    validates: [(val) => validateHexalColor(val, "Bundle title")],
                }),
                bundle_title_style: useField(_config?.bundles_design?.bundle_title_style),
                bundle_title_size: useField({
                    value: _config?.bundles_design?.bundle_title_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Bundle title font size")],
                }),
                bundle_widget_bg: useField({
                    value: _config?.bundles_design?.bundle_widget_bg as unknown as string,
                    validates: [(val) => validateHexalColor(val, "Bundle widget")],
                }),
                bundle_widget_font_family: useField(_config?.bundles_design?.bundle_widget_font_family),
                product_background_color: useField({
                    value: _config?.bundles_design?.product_background_color,
                    validates: [(val) => validateHexalColor(val, "Product background")],
                }),
                product_border_color: useField({
                    value: _config?.bundles_design?.product_border_color,
                    validates: [(val) => validateHexalColor(val, "Product border")],
                }),
                product_name_color: useField({
                    value: _config?.bundles_design?.product_name_color,
                    validates: [(val) => validateHexalColor(val, "Product name")],
                }),
                product_name_style: useField(_config?.bundles_design?.product_name_style),
                product_name_size: useField({
                    value: _config?.bundles_design?.product_name_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Product name font")],
                }),
                product_price_color: useField({
                    value: _config?.bundles_design?.product_price_color,
                    validates: [(val) => validateHexalColor(val, "Product price")],
                }),
                product_price_style: useField(_config?.bundles_design?.product_price_style),
                product_price_size: useField({
                    value: _config?.bundles_design?.product_price_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Product price font")],
                }),
                variant_selector_text_size: useField({
                    value: (_config?.bundles_design?.variant_selector_text_size as unknown as string) ?? 12,
                    validates: [(val) => validateFontSize(val, "Variant Selector size")],
                }),
                variant_selector_text_color: useField({
                    value: _config?.bundles_design?.variant_selector_text_color ?? "#383838",
                    validates: [(val) => validateHexalColor(val, "Variant Selector")],
                }),
                variant_selector_text_style: useField(
                    _config?.bundles_design?.variant_selector_text_style ?? "450 normal",
                ),

                product_img_size: useField(_config?.bundles_design?.product_img_size),
                product_img_show: useField(_config?.bundles_design?.product_img_show),
                product_img_border_radius: useField(_config?.bundles_design?.product_img_border_radius),
                border_radius: useField(_config?.bundles_design?.border_radius as unknown as string),
                border_color: useField({
                    value: _config?.bundles_design?.border_color,
                    validates: [(val) => validateHexalColor(val, "Product border")],
                }),
                border_width: useField({
                    value: _config?.bundles_design?.border_width as unknown as string,
                    validates: [
                        (val) =>
                            validateFontSize(val, "Border width font", {
                                min: 0,
                                max: 15,
                            }),
                    ],
                }),
                background_color: useField({
                    value: _config?.bundles_design?.background_color,
                    validates: [(val) => validateHexalColor(val, "Product background")],
                }),
                highlighted_offer_tag_color: useField({
                    value: _config?.bundles_design?.highlighted_offer_tag_color,
                    validates: [(val) => validateHexalColor(val, "Highlighted offer tag")],
                }),
                highlighted_offer_tag_style: useField(_config?.bundles_design?.highlighted_offer_tag_style),
                highlighted_offer_tag_size: useField({
                    value: _config?.bundles_design?.highlighted_offer_tag_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Offer tag font")],
                }),
                highlighted_price_tag_color: useField({
                    value: _config?.bundles_design?.highlighted_price_tag_color,
                    validates: [(val) => validateHexalColor(val, "Highlighted price tag")],
                }),
                highlighted_price_tag_style: useField(_config?.bundles_design?.highlighted_price_tag_style),
                highlighted_price_tag_size: useField({
                    value: _config?.bundles_design?.highlighted_price_tag_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Price tag font")],
                }),
                special_text_color: useField({
                    value: _config?.bundles_design.special_text_color,
                    validates: [(val) => validateHexalColor(val, "Special text")],
                }),
                special_text_background_color: useField({
                    value: _config?.bundles_design.special_text_background_color,
                    validates: [(val) => validateHexalColor(val, "Special text background")],
                }),
                special_text_font_size: useField({
                    value: _config?.bundles_design.special_text_font_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Special text font")],
                }),
                special_text_style: useField(_config?.bundles_design.special_text_style),
                total_footer_text_color: useField({
                    value: _config?.bundles_design?.total_footer_text_color,
                    validates: [(val) => validateHexalColor(val, "Total footer text")],
                }),
                total_footer_text_style: useField(_config?.bundles_design?.total_footer_text_style),
                total_footer_text_size: useField({
                    value: _config?.bundles_design?.total_footer_text_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Total footer text font")],
                }),
                total_footer_price_text_color: useField({
                    value: _config?.bundles_design?.total_footer_price_text_color,
                    validates: [(val) => validateHexalColor(val, "Total footer price text")],
                }),
                total_footer_price_text_style: useField(_config?.bundles_design?.total_footer_price_text_style),
                total_footer_price_text_size: useField({
                    value: _config?.bundles_design?.total_footer_price_text_size as unknown as string,
                    validates: [(val) => validateFontSize(val, "Total footer price font")],
                }),
                total_footer_background_color: useField({
                    value: _config?.bundles_design?.total_footer_background_color,
                    validates: [(val) => validateHexalColor(val, "Total footer background")],
                }),
                total_footer_background_style: useField(_config?.bundles_design?.total_footer_background_style),
                product_img_border_width: useField({
                    value: _config?.bundles_design?.product_img_border_width,
                    validates: [(val) => validateFontSize(val, "Product image border width", { min: 0, max: 10 })],
                }),
                product_img_border_color: useField({
                    value: _config?.bundles_design?.product_img_border_color,
                    validates: [(val) => validateHexalColor(val, "Product image border")],
                }),

                free_gift_header_bg_color: useField({
                    value: _config?.bundles_design?.free_gift_header_bg_color,
                    validates: [(val) => validateHexalColor(val, "Free gift header background")],
                }),
                free_gift_header_color: useField({
                    value: _config?.bundles_design?.free_gift_header_color,
                    validates: [(val) => validateHexalColor(val, "Free gift header text")],
                }),
                free_gift_header_size: useField({
                    value: _config?.bundles_design?.free_gift_header_size,
                    validates: [(val) => validateFontSize(val, "Free gift header text font")],
                }),
                free_gift_header_style: useField(_config?.bundles_design?.free_gift_header_style),
                free_gift_bg_color: useField({
                    value: _config?.bundles_design?.free_gift_bg_color,
                    validates: [(val) => validateHexalColor(val, "Free gift background")],
                }),
                free_gift_text_color: useField({
                    value: _config?.bundles_design?.free_gift_text_color,
                    validates: [(val) => validateHexalColor(val, "Free gift text")],
                }),
                free_gift_text_size: useField({
                    value: _config?.bundles_design?.free_gift_text_size,
                    validates: [(val) => validateFontSize(val, "Free gift text font")],
                }),
                free_gift_text_style: useField(_config?.bundles_design?.free_gift_text_style),
                free_gift_border: useField<boolean>(_config?.bundles_design?.free_gift_border),
                free_gift_border_width: useField({
                    value: _config?.bundles_design?.free_gift_border_width,
                    validates: [(val) => validateFontSize(val, "Free gift border width", { min: 0, max: 10 })],
                }),
                free_gift_border_color: useField({
                    value: _config?.bundles_design?.free_gift_border_color,
                    validates: [(val) => validateHexalColor(val, "Free gift border")],
                }),
                footer_save_money_text_color: useField({
                    value: _config?.bundles_design?.footer_save_money_text_color,
                    validates: [(val) => validateHexalColor(val, "Footer save money text")],
                }),
                footer_save_money_text_size: useField({
                    value: _config?.bundles_design?.footer_save_money_text_size,
                    validates: [(val) => validateFontSize(val, "Footer save money text size")],
                }),
                footer_save_money_text_style: useField(_config?.bundles_design?.footer_save_money_text_style),
            },
            general_settings: {
                show_bundle_on_product_page: useField(_config?.general_settings?.show_bundle_on_product_page),
                show_multiple_bundles_on_product_page: useField(
                    _config?.general_settings?.show_multiple_bundles_on_product_page,
                ),
                redirect_after_adding_product_to_cart: useField<RedirectOptionsTypes>(
                    _config?.general_settings?.redirect_after_adding_product_to_cart || "",
                ),
                redirect_to_checkout: useField<boolean | undefined>(_config?.general_settings?.redirect_to_checkout),
                show_powered_by_kefi: useField<boolean | undefined>(_config?.general_settings?.show_powered_by_kefi),
                max_width: useField(_config?.general_settings?.max_width),
                override_max_width: useField(_config?.general_settings?.override_max_width),
                margin_top: useField(_config?.general_settings?.margin_top || 20),
                override_margin_top: useField(_config?.general_settings?.override_margin_top || false),
            },
            bundles_settings: {
                enable_stock_control: useField(_config?.bundles_settings?.enable_stock_control),
                out_of_stock_text: useField({
                    value: _config?.bundles_settings?.out_of_stock_text as string,
                    validates: [(val) => nonEmptyOverride(val, "Text is required")],
                }),
                listing_variant_with_image: useField(_config?.bundles_settings?.listing_variant_with_image),
                work_only_with_widget: useField(_config?.bundles_settings?.work_only_with_widget),
                bundle_position: useField<BundlePositionsTypes>(
                    _config?.bundles_settings?.bundle_position as BundlePositionsTypes,
                ),
                custom_selector: useField<string>(_config?.bundles_settings?.custom_selector || ""),
            },

            show_bundle_widget: useField<WidgetShowOptionsTypes>(_config?.show_bundle_widget),
            free_shipping: useField(_config?.free_shipping),
            animation_add_to_cart_btn: useField(_config?.animation_add_to_cart_btn),

            targeting: useField({
                value: _config.targeting,
                validates: [validateTargeting],
                dirtyStateComparator: (v1, v2) => {
                    return JSON.stringify(v1) != JSON.stringify(v2);
                },
            }),
        },
        onSubmit: async (fields) => {
            console._log(fields);
            return { status: "success" };
        },
    });
    const [selected, setSelected] = useState(0);
    const [fetchingBundle, setFetchingBundle] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const maxLength = isMobile ? MAX_LENGTH_MOBILE : MAX_LENGTH_DESKTOP;
    const { bId } = useParams();
    const goTo = useNavigate();
    const appConfigContext = useContext(AppConfigContext);
    const { sft, globalSettingsInfo, themeColorsInfo } = appConfigContext;
    const { globalSettings, loading } = globalSettingsInfo;
    const { mergeDesignWithThemeColors } = themeColorsInfo;

    const userSaved = globalSettingsInfo?.globalSettings?.design_settings_bxgy?.user_saved;

    useMemo(() => {
        setFormState({
            dirty,
            makeClean,
            validate,
        });
    }, [dirty, makeClean, validate]);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => {
            window.removeEventListener("resize", checkMobile);
        };
    }, []);

    useEffect(() => {
        if (!loading) {
            if (!forAiBundle && bId) {
                fetchBundle(bId);
            } else if (forAiBundle && preConfig) {
                fetchBundle(preConfig);
            } else {
                setFetchingBundle(false);
                const newEmbedId = generateRandomUniqueId();

                const mergedDesignSettings = mergeDesignWithThemeColors(globalSettings?.design_settings_bxgy);

                dispatchBundleConfig({
                    type: ChangeEvents.initState,
                    init: {
                        // @ts-expect-error hack
                        bundles_design: {
                            ...bundleConfig?.bundles_design,
                            ...(userSaved ? globalSettings?.design_settings_bxgy : mergedDesignSettings),
                        } as VolumeConfigInterface["bundles_design"],
                        general_settings: {
                            ...bundleConfig?.general_settings,
                            ...(globalSettings?.hasOwnProperty("general_settings")
                                ? {
                                      ...globalSettings?.general_settings,
                                      show_powered_by_kefi: globalSettings?.general_settings?.hasOwnProperty(
                                          "show_powered_by_kefi",
                                      )
                                          ? globalSettings?.general_settings?.show_powered_by_kefi
                                          : true,
                                  }
                                : {}),
                        },
                        bundles_settings: {
                            ...bundleConfig?.bundles_settings,
                            ...globalSettings?.bundle_settings_bxgy,
                        } as VolumeConfigInterface["bundles_settings"],
                        bundle_embed_id: newEmbedId,
                    },
                });
                // @ts-expect-error required
                setConfig((ps: any) => ({
                    ...bundleConfig,
                    bundles_design: {
                        ...ps.bundles_design,
                        ...(userSaved ? globalSettings?.design_settings_bxgy : mergedDesignSettings),
                    } as VolumeConfigInterface["bundles_design"],
                    general_settings: {
                        ...ps.general_settings,
                        ...(globalSettings?.hasOwnProperty("general_settings")
                            ? {
                                  ...globalSettings?.general_settings,
                                  show_powered_by_kefi: globalSettings?.general_settings?.hasOwnProperty(
                                      "show_powered_by_kefi",
                                  )
                                      ? globalSettings?.general_settings?.show_powered_by_kefi
                                      : true,
                              }
                            : {}),
                    },
                    bundles_settings: {
                        ...ps.bundles_settings,
                        ...globalSettings?.bundle_settings_bxgy,
                    } as VolumeConfigInterface["bundles_settings"],
                    bundle_embed_id: newEmbedId,
                }));
            }
        }
    }, [globalSettings, loading, bId]);

    useEffect(() => {
        if (!fetchingBundle) {
            setPreConfig({ ...bundleConfig });
            setSavingBundle && setSavingBundle(false);
        }
    }, [bundleConfig, fetchingBundle]);

    useEffect(() => {
        if (bundleConfig?.form_id && !forAiBundle) {
            goTo(getBundleRoute(BundleTypes.BXGY, bundleConfig?.form_id));
        }
    }, [bundleConfig?.form_id, forAiBundle]);

    const fetchBundle = async (id: string | null = "") => {
        if (!id) {
            shopify.saveBar.hide("save-bar");
            goTo(getBundleRoute(BundleTypes.BXGY));
        }
        setFetchingBundle(true);
        const res = await getData({ url: endPoints.getBundle + id });

        if (res.status == 200) {
            if (res?.data?.bundle?.form_id) {
                const newConfig = await updateBundleConfigWithRealTimeProducts(sft, [res.data.bundle]);

                const _c = {
                    ...bundleConfig,
                    ...res.data.bundle,
                    products: newConfig[0].products,
                    // @ts-expect-error required
                    discounted_products: newConfig[0].discounted_products,
                    publish_count: res.data.publish_count,
                    discount_label_template: res.data.bundle.discount_label_template ?? res.data.bundle.title,
                };
                _c.bundles_design = {
                    ...bundleConfig?.bundles_design,
                    ...(userSaved ? _c.bundles_design : mergeDesignWithThemeColors(_c.bundles_design)),
                };
                _c.general_settings = {
                    ...bundleConfig?.general_settings,
                    ..._c.general_settings,
                    show_powered_by_kefi: _c?.general_settings?.hasOwnProperty("show_powered_by_kefi")
                        ? _c?.general_settings?.show_powered_by_kefi
                        : true,
                };
                _c.targeting = { ..._c.targeting, ...getUpdatedTargeting(_c.targeting) };
                setShowBundlerLevel(_c?.bundle_product_level == "variant" ? true : false);
                dispatchBundleConfig({
                    type: ChangeEvents.initState,
                    init: {
                        ..._c,
                    },
                });
                setConfig({
                    ..._c,
                });
                setFetchingBundle(false);
                reset();
            } else {
                shopify.saveBar.hide("save-bar");
                if (forAiBundle) {
                    //goTo
                } else {
                    goTo(getBundleRoute(BundleTypes.BXGY));
                }
            }
        } else {
            shopify.saveBar.hide("save-bar");
            if (forAiBundle) {
                //goTo
            } else {
                goTo(getBundleRoute(BundleTypes.BXGY));
            }
        }
    };

    console._log("bundle id", bId, setIsMobile, maxLength);
    console._log("bundle bundleConfig?.products", bundleConfig?.products);
    console._log("bundle bundleConfig?.discounted_products", bundleConfig?.discounted_products);

    const handleNext = async (num: number, screen = "-") => {
        submit();
        if (!validate().length) {
            trackFromTabs({
                cta: "Form Tabs Switched",
                tabName: screen,
                bundleType: bundleConfig?.bundle_type,
            });
            setSelected(num);
        } else {
            scrollToTop();
        }
    };

    // --- Inspector mode -----
    const [inspectorMode, setInspectorMode] = useState(false);
    const hoveredElRef = useRef<HTMLElement | null>(null);
    const [selectedEl, setSelectedEl] = useState<HTMLElement | null>(null);
    const [inspectorTarget, setInspectorTarget] = useState<any>(null);
    const previewRef = useRef<HTMLDivElement>(null);

    const { handleInspectorClick } = useInspectorMode({
        inspectorMode,
        previewRef,
        selectedEl,
        setSelectedEl,
        hoveredElRef,
        setInspectorTarget,
        handleNext,
        bundleConfig,
    });

    return (
        <BxgyBundleContext.Provider
            value={{
                bundleConfig,
                dispatchBundleConfig,
                handleNext,
                fields,
                submit,
                isMobile,
                validate,
                SwatchesBundleConfigTouchedRef,

                // --- inspector mode additions ---
                inspectorMode,
                setInspectorMode,
                inspectorTarget,
            }}
        >
            {loading || fetchingBundle || bundleConfig?.selected_template_bxgy || bundleConfig?.form_id ? (
                <FormPreviewWrapper
                    oldBundleConfig={_config}
                    resetForm={reset}
                    bundleType={BundleTypes.BXGY}
                    forAiBundle={forAiBundle}
                    formPreviewWrapperRef={formPreviewWrapperRef}
                    onlyPreview={onlyPreview}
                    _setSavingBundle={setSavingBundle}
                    renderForms={[
                        <BxgyContentForm showBundlerLevel={showBundlerLevel} />,
                        <BxgyDesignForm />,
                        <BxgyTargetForm />,
                        <BxgySettingsForm />,
                    ]}
                    renderPreview={
                        <div
                            className={`${styles.previewContainer} ${inspectorMode ? styles.inspectorActive : ""}`}
                            ref={previewRef}
                            style={{
                                userSelect: inspectorMode ? "none" : "text",
                            }}
                            onClickCapture={handleInspectorClick}
                        >
                            <BxgyPreview
                                appConfigContext={appConfigContext}
                                bundleConfig={{
                                    ...bundleConfig,
                                    products: flattenProducts(
                                        bundleConfig.products,
                                        bundleConfig.should_flatten_products,
                                    ),
                                    discounted_products: flattenProducts(
                                        bundleConfig.discounted_products,
                                        bundleConfig.should_flatten_products,
                                    ),
                                    SwatchesBundleConfigTouchedRef: SwatchesBundleConfigTouchedRef,
                                }}
                            />
                        </div>
                    }
                    globalErrors={submitErrors}
                    submitForm={submit}
                    globalDirty={dirty}
                    dispatchBundleConfig={dispatchBundleConfig}
                    selected={selected}
                    setSelected={setSelected}
                    handleNext={handleNext}
                    bundleConfig={bundleConfig}
                    setConfig={setConfig}
                    validateForm={validate}
                    fetchBundle={fetchBundle}
                    loading={fetchingBundle || loading}
                    inspectorMode={inspectorMode}
                    setInspectorMode={setInspectorMode}
                />
            ) : (
                <TemplateSelectionScreen
                    bundleConfig={bundleConfig}
                    dispatchBundleConfig={dispatchBundleConfig}
                    makeClean={makeClean}
                />
            )}
        </BxgyBundleContext.Provider>
    );
};

export default Bxgy;
